import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import { useMediaQuery } from '@mui/material'

// Component Imports
import MailContent from '../../mailbox/MailContent'

import { getUrlParams } from '../../../services/urlServices'
import { getMessages, sendEmailReply } from '../../../services/B2BServices'
import { useToast } from '../../../../../campaigns/hooks/useToast'

const sortByDate = (arr, field, asc = true) => {
  return arr.sort((a, b) => asc
    ? (new Date(a?.[field]) - new Date(b?.[field]))
    : (new Date(b?.[field]) - new Date(a?.[field]))
  )
}

const getMessageObjExceptReplies = (message) => {
  return (({ replies, ...o }) => o)(message)
}

const getFlatMessages = (message, asc = true) => {
  let result = []
  result.push(getMessageObjExceptReplies(message))
  message?.replies?.length && message.replies.forEach((reply) => {
    const childFlat = getFlatMessages(reply)
    result = result.concat(childFlat)
  })

  return sortByDate(result, 'sentTimeMillis', asc)
}

// Threads arrive pre-sorted by the backend (root createdAt DESC). Sorting here would
// only reorder the current page and break the global ordering, so it is left alone.
const getManagedMessages = (arr) => {
  return arr?.map(item => getFlatMessages(item, false)) ?? []
}

const DEFAULT_PAGE = 0
const DEFAULT_PAGE_SIZE = 10
const DEFAULT_TOTAL_PAGE_COUNT = 0
const DEFAULT_TOTAL_RECORD_COUNT = 0

const Mailbox = () => {
  const { t } = useTranslation()

  const queryParams = getUrlParams()

  const { showSuccess, showError } = useToast()

  const b2bCampaignId = queryParams?.b2bCampaignId

  const [emailArrays, setEmailArrays] = useState([])
  const [activeEmailArr, setActiveEmailArr] = useState(null)
  const [fetching, setFetching] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [totalPageCount, setTotalPageCount] = useState(DEFAULT_TOTAL_PAGE_COUNT)
  const [totalRecordCount, setTotalRecordCount] = useState(DEFAULT_TOTAL_RECORD_COUNT)

  // Bumped by the refresh button to re-run the fetch with the same page and size
  const [refreshKey, setRefreshKey] = useState(0)

  // Hooks
  const isBelowMdScreen = useMediaQuery(theme => theme.breakpoints.down('md'))
  const isBelowSmScreen = useMediaQuery(theme => theme.breakpoints.down('sm'))

  // Reset paging when the campaign changes. Done during render rather than in an
  // effect so the fetch below never fires once with the previous campaign's page.
  const [renderedCampaignId, setRenderedCampaignId] = useState(b2bCampaignId)

  if (b2bCampaignId !== renderedCampaignId) {
    setRenderedCampaignId(b2bCampaignId)
    setCurrentPage(DEFAULT_PAGE)
    setActiveEmailArr(null)
  }

  const handlePageChange = (page) => {
    setActiveEmailArr(null)
    setCurrentPage(page)
  }

  // Changing the size shifts every thread boundary, so go back to the first page
  const handlePageSizeChange = (size) => {
    setActiveEmailArr(null)
    setPageSize(size)
    setCurrentPage(DEFAULT_PAGE)
  }

  const reloadMessages = () => setRefreshKey(key => key + 1)

  const handleSubmitMail = (email, successCallback, replyMessageId = null) => {
    setSubmitting(true)

    const replyPayload = {
      campaignId: b2bCampaignId,
      to: email.to,
      cc: email.cc,
      subject: email.subject,
      bodyHtml: email.html,
      replyMessageId
    }

    sendEmailReply(replyPayload).then(() => {
      showSuccess(t('mailTemplate.mailbox.send.toastSuccess'))
      successCallback()
    }).catch((err) => {
      err && showError(t('mailTemplate.mailbox.send.toastErr'))
    }).finally(() => {
      setSubmitting(false)
    })
  }

  useEffect(() => {
    if (!b2bCampaignId) return undefined

    // Set by the cleanup below when the page/size changes while a request is still
    // in flight. Without it, a slow response for the page the user just left would
    // resolve last and overwrite the page they are actually on.
    let ignore = false

    setFetching(true)

    getMessages({ b2bCampaignId }, currentPage, pageSize).then((res) => {
      if (ignore) return

      setEmailArrays(getManagedMessages(res?.data?.content))
      setTotalPageCount(res?.data?.totalPageCount ?? DEFAULT_TOTAL_PAGE_COUNT)
      setTotalRecordCount(res?.data?.totalRecordCount ?? DEFAULT_TOTAL_RECORD_COUNT)
    })
      .catch((err) => {
        err && !ignore && showError(t('mailTemplate.mailbox.getMessagesErr'))
      })
      .finally(() => {
        !ignore && setFetching(false)
      })

    return () => {
      ignore = true
    }
  }, [b2bCampaignId, currentPage, pageSize, refreshKey, showError, t])

  return (
    <div className='flex is-full overflow-hidden rounded relative' style={{ height: 'calc(100vh - 200px)', minHeight: '500px' }}>
      <MailContent
        fetching={fetching}
        isBelowMdScreen={isBelowMdScreen}
        isBelowSmScreen={isBelowSmScreen}
        emailArrays={emailArrays}
        activeEmailArr={activeEmailArr}
        setActiveEmailArr={setActiveEmailArr}
        reloadMessages={reloadMessages}
        currentPage={currentPage}
        pageSize={pageSize}
        totalPageCount={totalPageCount}
        totalRecordCount={totalRecordCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        submitting={submitting}
        handleSubmitReply={handleSubmitMail}
        onSubmitNewMail={handleSubmitMail}
      />
    </div>
  )
}

export default Mailbox
